import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connections } from '../../../../../../api/integrations'
import SydButton from '../../../../../commonDesign/Button'
import SydCheckbox from '../../../../../commonDesign/SydCheckbox'
import Card from '../../../../../molecules/Card'
import Alert from '../../../../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT, USER_TYPE } from '../../../../../../constants'
import { decipherError } from '../../../../../../utils/decipherError'
import SettingToggleRow from './SettingsRowToggle'

const USER_TYPE_OPTIONS = [
  { id: USER_TYPE.ADVISOR, label: 'Advisors' },
  { id: USER_TYPE.WEALTH_OWNER, label: 'Wealth Owners' }
]

const VALID_USER_TYPE_IDS = new Set(Object.values(USER_TYPE))

function parseAllowedTypes (raw) {
  if (raw == null || raw === '') return []
  const values = Array.isArray(raw) ? raw : String(raw).split(',')
  const parsed = new Set()
  for (const v of values) {
    const n = Number(String(v).trim())
    if (Number.isFinite(n) && VALID_USER_TYPE_IDS.has(n)) parsed.add(n)
  }
  return [...parsed].sort()
}

function serializeAllowedTypes (ids) {
  const parsed = parseAllowedTypes(ids)
  return parsed.length ? parsed.join(',') : null
}

function ConnectionSettings ({ connection }) {
  const { mutateAsync: modifyConnection, isLoading } = connections.useModifyIntegrationConnection()

  const [allowUserAuth, setAllowUserAuth] = useState(!!connection.allowUserAuth)
  const [allowedUserAuthTypes, setAllowedUserAuthTypes] = useState(
    () => parseAllowedTypes(connection.allowedUserAuthTypes)
  )
  const [useAsFallback, setUseAsFallback] = useState(!!connection.useAsFallback)
  const [saveError, setSaveError] = useState(null)

  const isConnectionDomain = (connection.domainClass || 'connection') === 'connection'

  useEffect(() => {
    setAllowUserAuth(!!connection.allowUserAuth)
    setAllowedUserAuthTypes(parseAllowedTypes(connection.allowedUserAuthTypes))
    setUseAsFallback(!!connection.useAsFallback)
    setSaveError(null)
  }, [connection.connectionId, connection.allowUserAuth, connection.allowedUserAuthTypes, connection.useAsFallback])

  const toggleUserType = useCallback((typeId) => (_e, checked) => {
    setAllowedUserAuthTypes(prev => {
      const next = new Set(prev)
      if (checked) next.add(typeId)
      else next.delete(typeId)
      return [...next].sort()
    })
  }, [])

  const handleSave = useCallback(async () => {
    try {
      setSaveError(null)
      await modifyConnection({
        connectionId: connection.connectionId,
        allowUserAuth: allowUserAuth ? 1 : 0,
        allowedUserAuthTypes: allowUserAuth ? serializeAllowedTypes(allowedUserAuthTypes) : null,
        useAsFallback: useAsFallback ? 1 : 0
      })
    } catch (err) {
      setSaveError(decipherError(err) || 'Failed to save connection settings. Please try again.')
    }
  }, [modifyConnection, connection.connectionId, allowUserAuth, allowedUserAuthTypes, useAsFallback])

  const handleReset = useCallback(() => {
    setAllowUserAuth(!!connection.allowUserAuth)
    setAllowedUserAuthTypes(parseAllowedTypes(connection.allowedUserAuthTypes))
    setUseAsFallback(!!connection.useAsFallback)
    setSaveError(null)
  }, [connection.allowUserAuth, connection.allowedUserAuthTypes, connection.useAsFallback])

  const allowedTypesHelper = useMemo(() => {
    if (!allowUserAuth) return null
    if (allowedUserAuthTypes.length === 0) return 'All user types can authenticate (no restriction).'
    const labels = USER_TYPE_OPTIONS.filter(o => allowedUserAuthTypes.includes(o.id)).map(o => o.label)
    return `Only ${labels.join(' and ')} can authenticate.`
  }, [allowUserAuth, allowedUserAuthTypes])

  const isDirty = useMemo(() => {
    const initialAllowed = parseAllowedTypes(connection.allowedUserAuthTypes)
    return (
      !!connection.allowUserAuth !== allowUserAuth ||
      !!connection.useAsFallback !== useAsFallback ||
      initialAllowed.length !== allowedUserAuthTypes.length ||
      initialAllowed.some((id, i) => id !== allowedUserAuthTypes[i])
    )
  }, [connection.allowUserAuth, connection.useAsFallback, connection.allowedUserAuthTypes,
    allowUserAuth, useAsFallback, allowedUserAuthTypes])

  if (!isConnectionDomain) {
    return null
  }

  return (
    <Card className='flex flex-col gap-sm'>
      <div className='flex items-center flex-wrap justify-between'>
        <div className='font-size-md font-bold'>Access</div>
        <div className='font-size-xs text-neutral-500'>How users connect to this integration</div>
      </div>

      <div className='border border-neutral-300 radius-md'>
        <SettingToggleRow
          title='User authentication'
          description='Allow users to connect their own credentials for this integration. Each user authenticates individually.'
          enabled={allowUserAuth}
          onChange={setAllowUserAuth}
          disabled={isLoading}
        />
        <div className='px-3 pb-3 flex flex-col gap-xs' style={{ marginLeft: '1rem' }}>
          <div className='font-size-xs text-neutral-500'>Who can authenticate</div>
          <div className='flex flex-wrap gap-md'>
            {USER_TYPE_OPTIONS.map(option => (
              <SydCheckbox
                key={`user-type-${option.id}`}
                label={option.label}
                value={allowedUserAuthTypes.includes(option.id)}
                onChange={toggleUserType(option.id)}
                disabled={!allowUserAuth || isLoading}
                size='small'
              />
            ))}
          </div>
          {allowedTypesHelper && (
            <div className='font-size-xs text-neutral-500'>{allowedTypesHelper}</div>
          )}
        </div>
        <SettingToggleRow
          style={{ borderTop: '1px solid var(--color-neutral-300)' }}
          title='Use as fallback'
          description='Fall back to this shared connection for users who have not set up their own credentials.'
          enabled={useAsFallback}
          onChange={setUseAsFallback}
          disabled={isLoading}
        />
      </div>

      {saveError ? (
        <Alert severity={ALERT_SEVERITY.error} variant={ALERT_VARIANT.standard}>
          {saveError}
        </Alert>
      ) : null}

      <div className='flex justify-between gap-sm'>
        <SydButton type='button' variant='secondary' onClick={handleReset} disabled={isLoading || !isDirty}>
          Reset
        </SydButton>
        <SydButton type='button' variant='primary' onClick={handleSave} processing={isLoading} disabled={!isDirty}>
          Save
        </SydButton>
      </div>
    </Card>
  )
}

ConnectionSettings.propTypes = {
  connection: PropTypes.shape({
    connectionId: PropTypes.number.isRequired,
    allowUserAuth: PropTypes.number,
    allowedUserAuthTypes: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.number)
    ]),
    useAsFallback: PropTypes.number,
    domainClass: PropTypes.string
  }).isRequired
}

export default ConnectionSettings
